<template>
  <section class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
    <div class="border-b border-gray-100 px-6 py-5 sm:px-8">
      <div class="flex items-center gap-3">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <icon name="calendar-days" class="h-5 w-5" />
        </span>
        <div>
          <h2 class="text-base font-semibold text-gray-900">
            Sloty wizyt
          </h2>
          <p class="mt-0.5 text-sm text-gray-500">
            Zaplanuj terminy dla podpiętych usług. Sloty zastępują dane z Medfile — na ich podstawie
            pacjent rezerwuje wizytę u tego specjalisty.
          </p>
        </div>
      </div>
    </div>

    <div class="px-6 py-6 sm:px-8">
      <div v-if="loading" class="py-10 text-center text-sm text-gray-500">
        Ładowanie slotów…
      </div>

      <div v-else-if="loadError" class="py-10 text-center text-sm text-red-600">
        {{ loadError }}
      </div>

      <!-- No manual services attached -->
      <div
        v-else-if="!services.length"
        class="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center"
      >
        <p class="text-sm font-medium text-gray-700">
          Brak podpiętych usług manualnych
        </p>
        <p class="mx-auto mt-1 max-w-md text-sm text-gray-500">
          Aby zaplanować sloty, najpierw podepnij usługę manualną do tego specjalisty
          (w edycji usługi, w polu „Specjaliści”).
        </p>
        <a
          :href="servicesHref"
          class="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
        >
          Przejdź do usług
          <icon name="cheveron-right" class="h-4 w-4 fill-current" />
        </a>
      </div>

      <div v-else class="space-y-6">
        <!-- Service selector -->
        <div class="flex flex-wrap gap-2">
          <button
            v-for="service in services"
            :key="service.id"
            type="button"
            :class="serviceTabClass(service.id)"
            @click="selectedServiceId = service.id"
          >
            {{ service.name }}
            <span class="ml-1.5 rounded-full bg-black/10 px-1.5 py-0.5 text-xs tabular-nums">
              {{ countForService(service.id) }}
            </span>
          </button>
        </div>

        <div
          v-if="serviceNeedsCategory"
          class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          Ta usługa nie ma kategorii, więc nie pojawi się w panelu rezerwacji.
          Ustaw kategorię w edycji usługi, zanim zaplanujesz sloty.
        </div>

        <!-- Add slot -->
        <div class="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
          <div class="flex flex-wrap items-end gap-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-600">Data</label>
              <input
                v-model="newDate"
                type="date"
                :min="todayIso"
                :max="maxDateIso"
                class="form-input py-1.5 text-sm"
              >
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-600">Godzina</label>
              <input
                ref="timeInput"
                v-model="newTime"
                type="time"
                step="300"
                class="form-input py-1.5 text-sm"
                @keyup.enter="addSlot"
              >
            </div>
            <button
              type="button"
              class="btn-indigo"
              :disabled="!canAdd || saving || serviceNeedsCategory"
              @click="addSlot"
            >
              <span aria-hidden="true" class="mr-1 text-base leading-none">+</span>
              Dodaj slot
            </button>
          </div>
          <p v-if="addError" class="mt-2 text-sm text-red-600">
            {{ addError }}
          </p>
        </div>

        <!-- Planned slots grouped by day -->
        <div v-if="groupedSlots.length" class="space-y-3">
          <div
            v-for="group in groupedSlots"
            :key="group.date"
            class="rounded-xl border border-gray-100 p-4"
          >
            <div class="mb-3 flex items-center gap-2">
              <icon name="calendar-days" class="h-4 w-4 text-gray-400" />
              <span class="text-sm font-semibold capitalize text-gray-800">
                {{ formatDate(group.date) }}
              </span>
              <span class="text-xs text-gray-400">· {{ group.slots.length }} slot(y)</span>
            </div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="slot in group.slots"
                :key="slot.id"
                class="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-sm font-medium text-indigo-700 tabular-nums"
              >
                {{ slot.time }}
                <button
                  type="button"
                  class="rounded-full p-0.5 text-indigo-400 transition-colors hover:bg-indigo-100 hover:text-red-600"
                  title="Usuń slot"
                  @click="removeSlot(slot)"
                >
                  <icon name="x" class="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          </div>
        </div>
        <div v-else class="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
          Brak zaplanowanych slotów dla tej usługi. Dodaj pierwszy termin powyżej.
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import Icon from '@/Shared/Icon.vue';
import { createServiceRouteHelper } from '@/utils/createServiceRouteHelper';

export default {
  name: 'SpecialistSlotsPanel',
  components: { Icon },
  props: {
    specialistId: {
      type: Number,
      required: true,
    },
  },
  data() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    return {
      loading: true,
      saving: false,
      loadError: '',
      addError: '',
      services: [],
      slots: [],
      selectedServiceId: null,
      // Prefill: today's date and the next full hour (current hour + 1).
      newDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      newTime: `${pad((now.getHours() + 1) % 24)}:00`,
    };
  },
  computed: {
    todayIso() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, '0');
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    },
    maxDateIso() {
      const date = new Date();
      date.setDate(date.getDate() + 365);
      const pad = (value) => String(value).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    selectedService() {
      return this.services.find((service) => service.id === this.selectedServiceId) || null;
    },
    serviceNeedsCategory() {
      return Boolean(this.selectedService && !this.selectedService.category);
    },
    canAdd() {
      return Boolean(this.selectedServiceId && this.newDate && this.newTime && !this.serviceNeedsCategory);
    },
    servicesHref() {
      return createServiceRouteHelper(null, this.$routes).index();
    },
    slotsForService() {
      return this.slots.filter((slot) => slot.service_id === this.selectedServiceId);
    },
    groupedSlots() {
      const byDate = {};
      this.slotsForService.forEach((slot) => {
        (byDate[slot.date] ||= []).push(slot);
      });
      return Object.keys(byDate)
        .sort()
        .map((date) => ({
          date,
          slots: byDate[date].sort((a, b) => a.time.localeCompare(b.time)),
        }));
    },
  },
  mounted() {
    this.fetchSlots();
  },
  methods: {
    csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    },
    countForService(serviceId) {
      return this.slots.filter((slot) => slot.service_id === serviceId).length;
    },
    serviceTabClass(serviceId) {
      const base = 'inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium transition-colors';
      return serviceId === this.selectedServiceId
        ? `${base} bg-indigo-600 text-white`
        : `${base} bg-gray-100 text-gray-700 hover:bg-gray-200`;
    },
    formatDate(isoDate) {
      const date = new Date(`${isoDate}T00:00:00`);
      return date.toLocaleDateString('pl-PL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
    async fetchSlots() {
      this.loading = true;
      this.loadError = '';
      try {
        const { data } = await axios.get(
          this.$routes.admin_specialist_slots(this.specialistId),
          { headers: { Accept: 'application/json' } },
        );
        this.services = data.services || [];
        this.slots = data.slots || [];
        if (this.services.length && !this.services.some((s) => s.id === this.selectedServiceId)) {
          this.selectedServiceId = this.services[0].id;
        }
      } catch {
        this.loadError = 'Nie udało się załadować slotów.';
      } finally {
        this.loading = false;
      }
    },
    async addSlot() {
      if (!this.canAdd || this.saving) return;
      this.saving = true;
      this.addError = '';
      try {
        const { data } = await axios.post(
          this.$routes.admin_specialist_slots(this.specialistId),
          { service_id: this.selectedServiceId, date: this.newDate, times: [this.newTime] },
          { headers: { 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' } },
        );
        if (data.created?.length) {
          this.slots = [...this.slots, ...data.created];
          this.newTime = '';
          this.$nextTick(() => this.$refs.timeInput?.focus());
        }
        if (data.errors?.length) {
          this.addError = data.errors.join(' ');
        }
      } catch (error) {
        this.addError = error.response?.data?.error || 'Nie udało się dodać slotu.';
      } finally {
        this.saving = false;
      }
    },
    async removeSlot(slot) {
      try {
        await axios.delete(
          this.$routes.admin_specialist_slot(this.specialistId, slot.id),
          { headers: { 'X-CSRF-Token': this.csrfToken() } },
        );
        this.slots = this.slots.filter((item) => item.id !== slot.id);
      } catch {
        this.addError = 'Nie udało się usunąć slotu.';
      }
    },
  },
};
</script>
